// ============================================================
// hooks/useSearchData.js
// 통합검색 상태/로직. 계산은 백엔드(API)에서 수행, 여기선 호출·상태관리만.
//
// [핵심 변경]
//  - 클라이언트 필터 제거 → API(list/allKeys/export/detail) 호출
//  - mutation 3-state(include/exclude) + 유전자별 hgvsp_short 2차 필터
//  - VAF/impact 필터
//  - 선택(selected)은 groupKey 기준, 페이지 이동해도 유지
//  - 전체선택 = 필터된 전체 groupKeys (안 보이는 것까지)
//  - Export: 선택 그룹 → 백엔드 요약 → 호출부(UI)에서 xlsx 2시트
// ============================================================
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
    API,
    MUT_REP_GENES, CNV_REP_GENES, ORGAN_SUBTYPES,
} from "../data/searchData";

// 한 페이지 기본 10개. 예전에는 창 높이에 맞춰 자동 계산했는데,
// 사용자가 10/20/50 중에서 고르도록 바꿨다(2026-09-17).
const PAGE_SIZE_DEFAULT = 10;

const EMPTY_CHIPS = {
    organ: [], sampleNT: [], subtype: [], cro: ["yes"], msi: [],
    drug: [], paired: [], immune: [], caf: [],
    gender: [], dxType: [],
    txDrug: [], txStage: [],
};
const EMPTY_RANGES = {};
// CNV: [{ gene, type:'Amplification'|'Deletion' }]  (유전자 선택 후 amp/del 지정)
const EMPTY_CNV = [];
// mutation 3-state: { EGFR: "include" | "exclude" }  (없으면 해제)
const EMPTY_MUT = {};
// 선택 유전자별 hgvsp_short: { EGFR: ["p.L858R", ...] }
const EMPTY_HGVSP = {};

async function postJSON(url, body) {
    const res = await fetch(url, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        // 401 = 미인증/세션만료. 서버가 내려주는 안내 문구를 그대로 노출한다.
        let msg = `${url} ${res.status}`;
        try { const j = await res.json(); if (j && j.error) msg = j.error; } catch (e) { /* 본문 없음 */ }
        throw new Error(msg);
    }
    return res.json();
}

export function useSearchData() {
    const [chips, setChips] = useState(EMPTY_CHIPS);
    const [ranges, setRanges] = useState(EMPTY_RANGES);
    const [mutState, setMutState] = useState(EMPTY_MUT);       // 3-state
    const [hgvsp, setHgvsp] = useState(EMPTY_HGVSP);           // 유전자별 변이
    const [cnv, setCnv] = useState(EMPTY_CNV);                 // [{gene,type}]
    const [sort, setSort] = useState({ key: "id", dir: "asc" });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
    const [selectedDrug, setSelectedDrug] = useState(null);
    const [cohorts, setCohorts] = useState([]);

    // 서버 결과
    const [rows, setRows] = useState([]);
    const [totalGroups, setTotalGroups] = useState(0);
    const [filterOptions, setFilterOptions] = useState({ subtypes: [], mutations: [], mutGenes: [], cnvGenes: [], hgvspByGene: {}, fusionGenes: [], drugs: [], txDrugs: [], txResponses: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // 데이터 기준 시각(백엔드 캐시가 구워진 시각) + 낡은 캐시 여부
    const [dataAsOf, setDataAsOf] = useState(null);
    const [dataStale, setDataStale] = useState(false);

    // 선택: groupKey Set (페이지 넘어가도 유지)
    const [selectedKeys, setSelectedKeys] = useState(() => new Set());
    // 전체선택(필터 전체) 상태
    const [allFilteredSelected, setAllFilteredSelected] = useState(false);

    /* ---------- 동적 옵션 (백엔드 우선, 없으면 정적 기본값) ---------- */
    // subtype 은 DB 값이 정본이다. 정적 ORGAN_SUBTYPES 는 정형화 이전 표기라
    // 폴백으로 쓰면 클릭 시 매칭 0건이 되므로 사용하지 않는다.
    const dynamicSubtypes = useMemo(() => filterOptions.subtypes || [], [filterOptions.subtypes]);

    // 검색용 전체 유전자 (백엔드 mutations = 필터결과 존재 유전자). 없으면 대표만.
    const dynamicMutGenes = useMemo(() => {
        return filterOptions.mutations?.length ? filterOptions.mutations : MUT_REP_GENES;
    }, [filterOptions.mutations]);
    const dynamicCnvGenes = useMemo(() => {
        return filterOptions.cnvGenes?.length ? filterOptions.cnvGenes : CNV_REP_GENES;
    }, [filterOptions.cnvGenes]);

    // 장기 목록의 정본도 백엔드다. 정적 ORGAN_SUBTYPES 는 6종뿐이라
    // 방광·분변·유산균 등 새 장기가 들어오면 필터에 뜨지 않는다.
    const dynamicOrgans = useMemo(() => {
        return filterOptions.organs?.length ? filterOptions.organs : Object.keys(ORGAN_SUBTYPES);
    }, [filterOptions.organs]);

    // 대표 유전자(버튼 고정 노출)의 정본은 백엔드 ORGAN_MUTATIONS 다.
    // searchData.js 의 MUT_REP_GENES 는 백엔드 응답이 오기 전 첫 렌더용 폴백으로만 쓴다.
    const dynamicMutRepGenes = useMemo(() => {
        return filterOptions.mutRepGenes?.length ? filterOptions.mutRepGenes : MUT_REP_GENES;
    }, [filterOptions.mutRepGenes]);

    // 백엔드가 organ 필터를 반영해 내려주는 목록만 사용한다.
    // 정적 DRUGS 로 폴백하면 폐암 약(Osimertinib 등)이 모든 장기에서 노출된다.
    const dynamicDrug = useMemo(() => filterOptions.drugs || [], [filterOptions.drugs]);

    const dynamicTxDrug = useMemo(() => filterOptions.txDrugs || [], [filterOptions.txDrugs]);

    // hgvsp 후보: include 로 고른 유전자의 변이 목록 (백엔드 filterOptions.hgvspByGene)
    const includedGenes = useMemo(
        () => Object.keys(mutState).filter((g) => mutState[g] === "include"),
        [mutState]
    );

    /* ---------- 필터 페이로드 구성 ---------- */
    const buildFilters = useCallback(() => {
        const mutationInclude = Object.keys(mutState).filter((g) => mutState[g] === "include");
        const mutationExclude = Object.keys(mutState).filter((g) => mutState[g] === "exclude");
        const hgvspShort = includedGenes.flatMap((g) => hgvsp[g] || []);
        return {
            organ: chips.organ, sampleNT: chips.sampleNT, subtype: chips.subtype,
            cro: chips.cro, msi: chips.msi,
            mutationInclude, mutationExclude, hgvspShort,
            cnv: cnv.length ? cnv : undefined,            // [{gene,type}]
            drug: chips.drug,
            paired: chips.paired, immune: chips.immune, caf: chips.caf,
            gender: chips.gender, dxType: chips.dxType,
            // 환자 치료 이력 (txStage: pre | post | naive)
            txDrug: chips.txDrug, txStage: chips.txStage,
        };
    }, [chips, mutState, hgvsp, includedGenes, cnv]);

    /* ---------- 목록 조회 ---------- */
    const reqIdRef = useRef(0);
    useEffect(() => {
        const myReq = ++reqIdRef.current;
        setLoading(true);
        setError(null);
        postJSON(API.list, {
            filters: buildFilters(),
            drug: selectedDrug,
            sort,
            page,
            pageSize,
        })
            .then((data) => {
                if (myReq !== reqIdRef.current) return; // 최신 요청만 반영
                setRows(data.rows || []);
                setTotalGroups(data.totalGroups || 0);
                setDataAsOf(data.dataAsOf || null);
                setDataStale(!!data.stale);
                if (data.filterOptions) setFilterOptions(data.filterOptions);
            })
            .catch((e) => {
                if (myReq !== reqIdRef.current) return;
                setError(e.message);
                setRows([]);
                setTotalGroups(0);
            })
            .finally(() => {
                if (myReq === reqIdRef.current) setLoading(false);
            });
    }, [buildFilters, selectedDrug, sort, page, pageSize]);

    // 약물은 자동 선택하지 않는다. 기본값은 "NA"(선택 없음)다.
    // 예전에는 목록 첫 약물을 자동으로 골라, 사용자가 고르지도 않은 약물의 IC50 이
    // 컬럼에 떠 있었다(2026-09-17 연구원 피드백으로 제거).

    const totalPages = Math.max(1, Math.ceil(totalGroups / pageSize));
    const safePage = Math.min(page, totalPages);

    /* ---------- 액션: chips ---------- */
    const toggleChip = useCallback((key, value, single) => {
        setChips((prev) => {
            const cur = prev[key] || [];
            let next;
            if (single) {
                // 단일 선택: 같은 값 재클릭 시 해제, 아니면 교체
                next = cur.includes(value) ? [] : [value];
            } else {
                next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
            }
            // 약물 필터: 방금 "추가"한 약물을 IC50 표시 약물(selectedDrug)로 자동 설정
            if (key === "drug" && !cur.includes(value)) {
                setSelectedDrug(value);
            }
            return { ...prev, [key]: next };
        });
        setPage(1);
        setAllFilteredSelected(false);
    }, []);

    const setRange = useCallback((key, val) => {
        setRanges((prev) => ({ ...prev, [key]: val }));
        setPage(1);
        setAllFilteredSelected(false);
    }, []);

    /* ---------- 액션: mutation 3-state ---------- */
    // 클릭 순환: (없음) → include → exclude → (없음)
    const cycleMutation = useCallback((gene) => {
        setMutState((prev) => {
            const cur = prev[gene];
            const next = { ...prev };
            if (!cur) next[gene] = "include";
            else if (cur === "include") next[gene] = "exclude";
            else delete next[gene];
            return next;
        });
        // exclude/해제된 유전자의 hgvsp 정리는 아래 useEffect([mutState]) 가 담당한다.
        // (여기서 setHgvsp 로 사본만 만들면 아무것도 지우지 못하면서 리렌더만 한 번 더 일어난다)
        setPage(1);
        setAllFilteredSelected(false);
    }, []);

    // hgvsp_short 토글 (특정 유전자의 변이 체크)
    const toggleHgvsp = useCallback((gene, variant) => {
        setHgvsp((prev) => {
            const cur = prev[gene] || [];
            const next = cur.includes(variant) ? cur.filter((v) => v !== variant) : [...cur, variant];
            const n = { ...prev, [gene]: next };
            if (next.length === 0) delete n[gene];
            return n;
        });
        setPage(1);
        setAllFilteredSelected(false);
    }, []);

    // include 해제된 유전자의 hgvsp 는 자동 정리
    useEffect(() => {
        setHgvsp((prev) => {
            const n = {};
            for (const g of Object.keys(prev)) {
                if (mutState[g] === "include") n[g] = prev[g];
            }
            return n;
        });
    }, [mutState]);

    const clearAll = useCallback(() => {
        setChips(EMPTY_CHIPS);
        setRanges(EMPTY_RANGES);
        setMutState(EMPTY_MUT);
        setHgvsp(EMPTY_HGVSP);
        setCnv(EMPTY_CNV);
        setPage(1);
        setAllFilteredSelected(false);
    }, []);

    /* CNV 조작: 유전자 추가(기본 Amplification) / 타입 변경 / 제거 */
    const addCnvGene = useCallback((gene) => {
        setCnv((prev) => prev.some((c) => c.gene === gene) ? prev : [...prev, { gene, type: "Amplification" }]);
        setPage(1);
    }, []);
    const setCnvType = useCallback((gene, type) => {
        setCnv((prev) => prev.map((c) => c.gene === gene ? { ...c, type } : c));
        setPage(1);
    }, []);
    const removeCnvGene = useCallback((gene) => {
        setCnv((prev) => prev.filter((c) => c.gene !== gene));
        setPage(1);
    }, []);

    const toggleSort = useCallback((key) => {
        setSort((prev) => {
            if (prev.key !== key) return { key, dir: "asc" };       // 새 컬럼 → 오름차순
            if (prev.dir === "asc") return { key, dir: "desc" };    // 오름 → 내림
            return { key: "id", dir: "asc" };                       // 내림 → 해제(기본 GBCC순)
        });
    }, []);

    /* ---------- 코호트 ---------- */
    const applyCohort = useCallback((c) => {
        setChips({ ...EMPTY_CHIPS, ...(c.chips || {}) });
        setRanges({ ...EMPTY_RANGES, ...(c.ranges || {}) });
        setMutState({ ...(c.mutState || {}) });
        setHgvsp({ ...(c.hgvsp || {}) });
        // CNV 도 코호트에 포함된다. 복원하지 않으면 직전 CNV 필터가 그대로 남아 엉뚱한 조합이 된다.
        setCnv(Array.isArray(c.cnv) ? c.cnv : EMPTY_CNV);
        if (c.drug) setSelectedDrug(c.drug);
        setPage(1);
        setAllFilteredSelected(false);
    }, []);
    const addCohort = useCallback((name) => {
        setCohorts((prev) => [...prev, { id: Date.now(), name, chips, ranges, mutState, hgvsp, cnv, drug: selectedDrug }]);
    }, [chips, ranges, mutState, hgvsp, cnv, selectedDrug]);
    const removeCohort = useCallback((id) => {
        setCohorts((prev) => prev.filter((c) => c.id !== id));
    }, []);

    /* ---------- 선택 (groupKey 기준, 페이지 넘어가도 유지) ---------- */
    const toggleOne = useCallback((groupKey) => {
        setSelectedKeys((prev) => {
            const n = new Set(prev);
            n.has(groupKey) ? n.delete(groupKey) : n.add(groupKey);
            return n;
        });
        setAllFilteredSelected(false);
    }, []);

    // 현재 페이지 전체 토글
    const togglePage = useCallback(() => {
        setSelectedKeys((prev) => {
            const n = new Set(prev);
            const pageKeys = rows.map((r) => r.groupKey);
            const allOn = pageKeys.every((k) => n.has(k));
            if (allOn) pageKeys.forEach((k) => n.delete(k));
            else pageKeys.forEach((k) => n.add(k));
            return n;
        });
        setAllFilteredSelected(false);
    }, [rows]);

    // 필터된 "전체" 선택 (안 보이는 것까지) — allKeys API
    const selectAllFiltered = useCallback(async () => {
        try {
            const data = await postJSON(API.allKeys, { filters: buildFilters(), drug: selectedDrug });
            setSelectedKeys(new Set(data.groupKeys || []));
            setAllFilteredSelected(true);
        } catch (e) {
            setError(e.message);
        }
    }, [buildFilters, selectedDrug]);

    const clearSelection = useCallback(() => {
        setSelectedKeys(new Set());
        setAllFilteredSelected(false);
    }, []);

    /* ---------- Export (선택 그룹 → 백엔드 요약 반환) ---------- */
    const fetchExportData = useCallback(async () => {
        const keys = Array.from(selectedKeys);
        if (keys.length === 0) return null;
        // 엑셀 첫 시트(화면 컬럼)의 IC50·Mutation 칸은 화면과 같이 "고른 것만" 채운다.
        //   selectedDrugs : 필터에서 고른 약물 + IC50 컬럼에서 고른 약물
        //   mutationInclude : 포함(include)으로 고른 유전자
        const drugs = Array.from(new Set([...(chips.drug || []), ...(selectedDrug ? [selectedDrug] : [])]));
        return postJSON(API.export, {
            groupKeys: keys,
            selectedDrugs: drugs,
            mutationInclude: includedGenes,
        });
    }, [selectedKeys, chips.drug, selectedDrug, includedGenes]);

    /* ---------- 상세 ---------- */
    const fetchDetail = useCallback(async (groupKey, sampleName) => {
        const selectedGenes = Object.keys(mutState);   // 포함/제외로 고른 유전자 → 상세에서 강조
        return postJSON(API.detail, { groupKey, sampleName, selectedGenes });
    }, [mutState]);

    /* ---------- 파생값 ---------- */
    const totalActive = useMemo(() => {
        const chipN = Object.values(chips).reduce((s, a) => s + a.length, 0);
        const rangeN = Object.values(ranges).filter(Boolean).length;
        const mutN = Object.keys(mutState).length;
        const hgvspN = Object.values(hgvsp).reduce((s, a) => s + a.length, 0);
        return chipN + rangeN + mutN + hgvspN;
    }, [chips, ranges, mutState, hgvsp]);

    const pageAllSelected = rows.length > 0 && rows.every((r) => selectedKeys.has(r.groupKey));
    const pageSomeSelected = !pageAllSelected && rows.some((r) => selectedKeys.has(r.groupKey));

    return {
        loading, error, dataAsOf, dataStale,
        rows, filteredCount: totalGroups,
        page: safePage, totalPages, pageSize, setPage, setPageSize,
        chips, ranges, sort,
        toggleChip, setRange, clearAll, applyCohort, toggleSort,
        totalActive, totalSamples: totalGroups,
        cohorts, addCohort, removeCohort,
        selectedDrug, setSelectedDrug,
        dynamicSubtypes, dynamicMutGenes, dynamicCnvGenes, dynamicDrug, filterOptions,
        dynamicTxDrug, dynamicMutRepGenes, dynamicOrgans,

        // mutation 3-state + hgvsp
        mutState, cycleMutation, hgvsp, toggleHgvsp, includedGenes,

        // CNV (유전자 + amp/del)
        cnv, addCnvGene, setCnvType, removeCnvGene,

        // 선택
        selectedKeys, toggleOne, togglePage, selectAllFiltered, clearSelection,
        pageAllSelected, pageSomeSelected, allFilteredSelected,
        selectedCount: selectedKeys.size,

        // export / detail
        fetchExportData, fetchDetail,
    };
}